import { useTranslation } from 'react-i18next'
import { ButtonLink, Card, EmptyState, PhotoStep, PhotoSteps, Skeleton, StatusChip } from '@/ds'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { featureEnabled } from '@/config/features'
import { telHref } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { useAsync } from '@/lib/useAsync'
import { accesosService } from '@/services/accesos'
import type { PhotoStepData } from '@/services/accesos'
import type { GuideItem } from '@/services/reserva'
import { SectionError } from '../Estancia/sections/SectionError'
import { AccessBack, EmergencyLine, emergencyPhone, placeName } from './shared'
import { usePhotos } from './usePhotos'

/** Pasos con foto; sin foto guardada (o sin conexión y sin caché) se ve el marcador con su descripción. */
export function PhotoStepList({ steps }: { steps: PhotoStepData[] }) {
  const photos = usePhotos(steps.flatMap((s) => (s.photoUrl ? [s.photoUrl] : [])))
  return (
    <PhotoSteps>
      {steps.map((s) => {
        const src = s.photoUrl ? photos[s.photoUrl] : null
        return <PhotoStep key={s.order} number={s.order} title={s.title} text={s.text} imageAlt={s.photoAlt} imageLabel={s.photoAlt} {...(src ? { image: src } : {})} />
      })}
    </PhotoSteps>
  )
}

/** Ítem de la guía de la propiedad tal cual, con sus imágenes (inventario D40 y D13). */
export function GuideItemBlock({ item }: { item: GuideItem }) {
  const photos = usePhotos(item.images)
  const shown = item.images.flatMap((url) => (photos[url] ? [photos[url]] : []))
  return (
    <Card className="accesos__card">
      <h2 className="accesos__row-title">{item.name}</h2>
      <p className="accesos__guide">{item.description}</p>
      {shown.length > 0 && (
        <div className="accesos__guide-images">
          {shown.map((src) => (
            <img key={src} src={src} alt="" />
          ))}
        </div>
      )}
    </Card>
  )
}

/**
 * 8.4 · Cómo entrar sin cerradura conectada (AccesosDetalle.dc.html `hotel`, Bloque 4 §4d). Mock: pasos
 * con foto y recepción. Real: el ítem de la guía sobre cómo entrar, tal cual (D40). Guardado sin conexión
 * (D18, D39).
 */
export function Instrucciones() {
  const { t } = useTranslation()
  const stay = useLoadedStay()
  const [data, reload] = useAsync(`ins:${stay.guestCode}`, (signal) => accesosService.getInstructions(stay, signal))
  const ins = data.kind === 'ready' ? data.value : null
  const showPhotoSteps = featureEnabled('accesosPasosFoto')
  const steps = showPhotoSteps ? (ins?.steps ?? []) : []
  const reception = showPhotoSteps ? ins?.reception ?? null : null
  const empty = ins !== null && steps.length === 0 && ins.guideItem === null
  const phone = ins?.emergencyPhone ?? emergencyPhone(stay)
  const lead = ins && !empty ? (steps.length > 0 ? t('v2.accesos.instrucciones.lead', { name: placeName(stay), count: steps.length }) : t('v2.accesos.instrucciones.leadGuia', { name: placeName(stay) })) : null
  return (
    <main className="page">
      <div className="accesos__head">
        <AccessBack />
        <h1>{t('v2.accesos.instrucciones.titulo')}</h1>
        {lead && <p className="page__lead">{lead}</p>}
      </div>
      {data.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={360} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {empty && (
        <>
          <EmptyState icon="book" title={t('v2.accesos.instrucciones.vacio')} headingLevel={2}>
            {t('v2.accesos.instrucciones.vacioTexto')}
          </EmptyState>
          {stay.contact.whatsapp ? (
            <ButtonLink variant="secondary" icon="message-circle" href={whatsappHref(stay.contact.whatsapp, stay.reference)} target="_blank" rel="noreferrer">
              {t('v2.ds.contact.whatsapp')}
            </ButtonLink>
          ) : (
            phone && (
              <ButtonLink variant="secondary" icon="phone" href={telHref(phone)}>
                {t('v2.ds.contact.llamar')}
              </ButtonLink>
            )
          )}
        </>
      )}
      {ins && !empty && (
        <>
          <div className="accesos__chips">
            {reception?.open24h && (
              <StatusChip tone="success" icon="check-circle">
                {t('v2.accesos.instrucciones.recepcion24h')}
              </StatusChip>
            )}
            {reception && !reception.open24h && reception.hours && (
              <StatusChip tone="neutral" icon="clock">
                {t('v2.accesos.instrucciones.recepcionHorario', { hours: reception.hours })}
              </StatusChip>
            )}
            <StatusChip tone="info" icon="wifi-off">
              {t('v2.accesos.instrucciones.guardadoSinConexion')}
            </StatusChip>
          </div>
          {steps.length > 0 && <PhotoStepList steps={steps} />}
          {ins.guideItem && <GuideItemBlock item={ins.guideItem} />}
          {reception?.afterHoursText && (
            <div className="accesos__box">
              {reception.afterHoursTitle && <h2 className="accesos__row-title">{reception.afterHoursTitle}</h2>}
              <p className="accesos__row-text">{reception.afterHoursText}</p>
            </div>
          )}
        </>
      )}
      <EmergencyLine phone={phone} />
    </main>
  )
}
